import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import {
  StopCard, Skeleton,
} from '@routific/hive';
import { useTranslation } from 'react-i18next';
import timestampToHrsMin from '../../../../../../helpers/timestampToHrsMin';
import { DateTimeFormat, RouteDtoModelType, RouteTimelineDtoModelType } from '../../../../../../store/mst/models';
import TimelineDot from '../../../../../shared/TimelineDot';
import { defaultTimeFormat, twelveHrsMinFormat } from '../../../../../../constants';
import { useStore } from '../../../../../../store/mst';
import { HeaderEllipsisCaption, HeaderTextStack } from './HeaderTextStyles';
import { getEventTimeLabel } from './getEventTimeLabel';

interface Props {
  route: RouteDtoModelType;
  event: RouteTimelineDtoModelType;
}

const CardWrapper = styled.div`
  cursor: pointer;
`;

const StartEndTimelineCard: React.FunctionComponent<Props> = ({ route, event }) => {
  const { ui, organization } = useStore();
  const { t } = useTranslation('components');

  const { isStartEvent } = event;
  const location = route.constraints?.[isStartEvent ? 'startLocations' : 'endLocations']?.[0];
  const startTime = event.eta?.actualArrivalTime || (isStartEvent ? event.departureTime : event.arrivalTime) || undefined;
  const formattedTime = organization.settings.dateTimeFormat === DateTimeFormat.MILITARY
    ? timestampToHrsMin(startTime, defaultTimeFormat)
    : timestampToHrsMin(startTime, twelveHrsMinFormat);

  let timeLabel = getEventTimeLabel(event, t);
  if (isStartEvent) {
    timeLabel = route.isStarted
      ? t('routeDetailsPanel.stats.labels.departed')
      : t('routeDetailsPanel.stats.labels.etd');
  }

  const handleClick = () => {
    ui.selectRouteEvent(event.id);
  };

  const labeledTimeDisplay = formattedTime ? `${timeLabel}: ${formattedTime}` : undefined;

  const marker = (
    <TimelineDot
      eventType={event.eventType}
      status={event.currentStatus?.status}
      color={route.color}
      altColor={event.orders?.[0]?.pendingColor}
    />
  );

  const header = (
    <HeaderTextStack>
      {labeledTimeDisplay && (
        <HeaderEllipsisCaption type="secondary">{labeledTimeDisplay}</HeaderEllipsisCaption>
      )}
      {location?.hasLoaded('address') ? (
        <HeaderEllipsisCaption ellipsis data-cy="start-end-timeline-card-address">
          {location.address}
        </HeaderEllipsisCaption>
      ) : (
        <Skeleton active paragraph={false} title={{ width: 200 }} />
      )}
    </HeaderTextStack>
  );

  return (
    <CardWrapper
      data-cy="start-end-timeline-card-detail"
      data-testid="start-end-timeline-card-detail"
      onClick={handleClick}
      onMouseEnter={() => { ui.hoverRouteEvent(event.id); }}
      onMouseLeave={() => { ui.hoverRouteEvent(undefined); }}
    >
      <StopCard
        header={header}
        marker={marker}
        isComplete={isStartEvent ? !!route.isStarted : !!route.finished}
        isSelected={event.isSelected}
      />
    </CardWrapper>
  );
};

export default observer(StartEndTimelineCard);
